/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, ChangeDetectionStrategy, OnInit, ViewChild, OnDestroy } from '@angular/core';
import {
	ContextMenuService,
	CONTEXT_MENUS,
	IContextMenu,
	IContextMenuData,
} from '../../context-menu.service';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { PlanState } from 'src/app/services/plan-state.service';
import * as strings from '@touchplan/strings';
import { TicketEditService } from 'src/app/modules/ticket-edit/ticket-edit.service';
import { Ticket } from '@touchplan/common';
import { MatMenuTrigger } from '@angular/material/menu';
import {
	ITicketDependencyEditData,
	TicketDependencyDialogComponent,
} from '../ticket-dependency-dialog/ticket-dependency-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DependencyLinkageType } from 'src/app/app-data/models/DependencyLinkageType';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { defaultLagDays, defaultLinkageType } from 'src/app/app-data/models/Dependencies';

@Component({
	selector: 'ticket-edit-context-menu',
	templateUrl: './ticket-edit-context-menu.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketEditContextMenuComponent implements IContextMenu, OnInit, OnDestroy {
	@ViewChild('contextMenu', { static: true }) public contextMenu;
	@ViewChild('ticketEditContextMenuTrigger') public trigger: MatMenuTrigger;

	public appStrings = strings;

	private logger: ILoggerService;

	public menuData$: Subject<IContextMenuData>;
	public dependencyAttributesFeatureFlag$: Observable<boolean>;

	private shutdownSubject$: Subject<void> = new Subject();
	public ticketCount = 1;

	private clickedTicket: Ticket;
	private timelineGroupMenu;
	public hasPredecessors$ = new BehaviorSubject<boolean>(null);

	constructor(
		private planState: PlanState,
		private contextMenuService: ContextMenuService,
		private ticketEditService: TicketEditService,
		private featureFlagManager: FeatureFlagManager,
		private dialog: MatDialog
	) {
		this.logger = LoggerServiceFactory.create('TicketEditContextMenuComponent');
	}

	public ngOnInit() {
		this.dependencyAttributesFeatureFlag$ = this.featureFlagManager
			.getFlag$(DynamicFlagTypes.TICKET_DEPENDENCY_LINKAGE_TYPE_AND_LAG)
			.pipe(takeUntil(this.shutdownSubject$));

		this.menuData$ = this.contextMenuService.register(CONTEXT_MENUS.TICKET_EDIT_CONFIRM_MENU, this);
		this.menuData$
			.pipe(
				map((menuData) => {
					if (menuData) {
						this.clickedTicket = menuData.data.tickets;
						this.timelineGroupMenu = menuData.data.timelineGroupMenu;
						return this.clickedTicket && this.planState.dependencies.hasPredecessor(this.clickedTicket);
					}
				}),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this.hasPredecessors$);

		this.planState.tickets.editTicket$.pipe(takeUntil(this.shutdownSubject$)).subscribe((ticketsInEdit) => {
			// When ticket edit canceled, close set predecessor menu
			if (ticketsInEdit.size === 0) {
				this.contextMenuService.hide(CONTEXT_MENUS.TICKET_EDIT_CONFIRM_MENU);
			}
		});
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.contextMenuService.remove(CONTEXT_MENUS.TICKET_EDIT_CONFIRM_MENU);

		this.menuData$ = null;
		this.ticketCount = null;
		this.clickedTicket = null;
	}

	public addPredecessor(): void {
		if (this.featureFlagManager.getFlag(DynamicFlagTypes.TICKET_DEPENDENCY_LINKAGE_TYPE_AND_LAG)) {
			this.displayEditPredecessorDialog(
				defaultLinkageType,
				defaultLagDays,
				false,
				(linkageType: DependencyLinkageType, lagDays: number): void => {
					this.planState.actions.simpleDependencyToggle(this.clickedTicket, linkageType, lagDays);
					this.ticketEditService.markFormAsDirty();
				}
			);
		} else {
			this.planState.actions.simpleDependencyToggle(this.clickedTicket);
			this.ticketEditService.markFormAsDirty();
		}
	}

	public removePredecessor(): void {
		this.planState.actions.simpleDependencyToggle(this.clickedTicket);
		this.ticketEditService.markFormAsDirty();
		this.timelineGroupMenu?.clear();
	}

	private displayEditPredecessorDialog(
		initialLinkageType: DependencyLinkageType,
		initialLag: number,
		displayCancelButton: boolean,
		handleSave: (linkageType: DependencyLinkageType, lagDays: number) => void,
		handleCancel?: () => void
	): void {
		const dialogRef = TicketDependencyDialogComponent.open(
			this.dialog,
			initialLinkageType,
			initialLag,
			displayCancelButton
		);
		dialogRef
			.afterClosed()
			.pipe(
				tap(() => this.timelineGroupMenu?.clear()),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((data: ITicketDependencyEditData) => {
				if (data) {
					handleSave(data.linkageType, data.lagDays);
				} else {
					if (handleCancel) {
						handleCancel();
					}
				}
			});
	}

	public editPredecessor(): void {
		const depData = this.planState.dependencies.getDependencyData(this.clickedTicket.$id);
		const initialLinkageType = depData?.linkageType ?? defaultLinkageType;
		const initialLag = depData?.lagDays ?? defaultLagDays;

		this.displayEditPredecessorDialog(
			initialLinkageType,
			initialLag,
			true,
			(possiblyChangedLinkageType: DependencyLinkageType, possiblyChangedLag: number): void => {
				if (depData?.linkageType !== possiblyChangedLinkageType || depData?.lagDays !== possiblyChangedLag) {
					this.updateDependency(this.clickedTicket, possiblyChangedLinkageType, possiblyChangedLag);
					this.ticketEditService.markFormAsDirty();
					this.timelineGroupMenu?.clear();
				}
			}
		);
	}

	private updateDependency(predecessorTicket: Ticket, linkageType: DependencyLinkageType, lagDays: number) {
		const successorTicketId = this.planState.dependencies.syncMode?.target;
		if (successorTicketId) {
			const successorTicket = this.planState.dependencies.checkTicketCache(successorTicketId);
			// Once to remove, a second time to add with the new data:
			// (1) Remove...
			this.toggleDependencyAndUpdateTicketLiveData(predecessorTicket, successorTicket, linkageType, lagDays);

			// (2) Add back...
			this.toggleDependencyAndUpdateTicketLiveData(predecessorTicket, successorTicket, linkageType, lagDays);
		}
	}

	private toggleDependencyAndUpdateTicketLiveData(
		predecessorTicket: Ticket,
		successorTicket: Ticket,
		linkageType: DependencyLinkageType,
		lagDays: number
	) {
		const predData = this.planState.dependencies.togglePredecessor(
			predecessorTicket.$id,
			linkageType,
			lagDays
		);
		if (predData) {
			this.planState.actions.updateTicketLiveData(
				successorTicket.$id,
				predData,
				successorTicket.isTimelineTicket ? this.planState.timelineTickets : this.planState.tickets
			);
		}
	}

	public onChanged(newData: IContextMenuData): void {
		this.menuData$.next(newData);
	}
}
